// client/src/dnd/emitter_tool/components/panels/EditorUIPanel.js
import React, { useEffect, useRef, useState } from "react";

/** Persisted keys */
const OPEN_KEY = "EmitterTool:EditorUIPanelOpenV1";

const UI_HIDE_FOG_KEY = "EmitterTool:UI:hideFog";
const UI_FIRE_MODE_KEY = "EmitterTool:UI:fireMode";
const UI_TIMED_DUR_KEY = "EmitterTool:UI:timedDur";
const UI_LAST_PLAY_KEY = "EmitterTool:UI:lastPlay";

const UI_WALLS_KEY = "EmitterTool:UI:wallsEnabled";
const UI_TOKEN_KEY = "EmitterTool:UI:tokenEnabled";
const UI_ENEMY_TOKEN_KEY = "EmitterTool:UI:enemyTokenEnabled";
const UI_GRID_KEY = "EmitterTool:UI:showGrid";
const UI_SHOW_SHAPE_KEY = "EmitterTool:UI:showShape";

const UI_GRID_SETTINGS_KEY = "EmitterTool:UI:gridSettingsV1";

const UI_PICKED_X_KEY = "EmitterTool:UI:pickedOriginX";
const UI_PICKED_Y_KEY = "EmitterTool:UI:pickedOriginY";
const UI_PICKED_ANGLE_KEY = "EmitterTool:UI:pickedAngleDeg";

const row = {
  display: "grid",
  gridTemplateColumns: "1fr auto",
  gap: 8,
  alignItems: "center",
  marginBottom: 8,
};
const inputStyle = {
  width: "100%",
  background: "#0b0d12",
  color: "white",
  border: "1px solid #374151",
  borderRadius: 8,
  padding: "6px 8px",
  fontSize: 12,
  boxSizing: "border-box",
};
const sliderStyle = { width: "100%", accentColor: "#64748b", cursor: "pointer" };
const label = { fontSize: 12, opacity: 0.9 };
const labelRow = { fontSize: 12, opacity: 0.85 };

function toNum(v, d = 0) { const n = Number(v); return Number.isFinite(n) ? n : d; }

function CollapsibleBox({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(OPEN_KEY);
      return saved == null ? defaultOpen : saved === "1";
    } catch {
      return defaultOpen;
    }
  });

  useEffect(() => {
    try { localStorage.setItem(OPEN_KEY, open ? "1" : "0"); } catch {}
  }, [open]);

  return (
    <div
      style={{
        border: "1px solid #334155",
        borderRadius: 8,
        marginBottom: 12,
        overflow: "hidden",
        background: "#0b0f17",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      <div
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "8px 10px",
          background: "#18212f",
          userSelect: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{title}</span>
        <span style={{ opacity: 0.75 }}>{open ? "▾" : "▸"}</span>
      </div>
      {open && (
        <div
          style={{
            padding: 8,
            boxSizing: "border-box",
            maxWidth: "100%",
            overflowX: "hidden",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function Slider({ label, value, onChange, min = 0, max = 10, step = 0.1, suffix = "", readOnly = false }) {
  const [text, setText] = useState(Number.isFinite(value) ? String(value) : String(min));

  useEffect(() => {
    const asStr = String(Number.isFinite(value) ? value : min);
    if (asStr !== text) setText(asStr);
  }, [value, min, max, readOnly]); // eslint-disable-line

  const commitNumber = (raw) => {
    if (readOnly) return;
    const parsed = Number(raw);
    const n = Number.isFinite(parsed) ? parsed : (Number.isFinite(value) ? value : min);
    const clamped = Math.max(min, Math.min(max, n));
    onChange?.(clamped);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
      <div style={{ ...labelRow, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}{suffix ? ` (${suffix})` : ""}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "0 0 auto" }}>
          <input
            type="number"
            value={text}
            min={min}
            max={max}
            step={step}
            readOnly={readOnly}
            onChange={(e) => setText(e.target.value)}
            onBlur={(e) => commitNumber(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            style={{
              width: "clamp(56px, 24%, 96px)",
              background: "#0b0d12",
              color: "white",
              border: "1px solid #374151",
              borderRadius: 6,
              padding: "4px 6px",
              fontSize: 12,
              textAlign: "right",
              boxSizing: "border-box",
              maxWidth: "100%",
              opacity: readOnly ? 0.7 : 1,
              cursor: readOnly ? "default" : "text",
            }}
          />
          {suffix ? <span style={{ opacity: 0.7, whiteSpace: "nowrap" }}>{suffix}</span> : null}
        </div>
      </div>

      <input
        type="range"
        value={Number.isFinite(value) ? value : min}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange?.(toNum(e.target.value, min))}
        style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", accentColor: "#64748b", cursor: "pointer", display: readOnly ? "none" : "block" }}
      />
    </div>
  );
}

export default function EditorUIPanel({
  wallsEnabled,
  setWallsEnabled,
  tokenEnabled,
  setTokenEnabled,
  enemyEnabled,
  setEnemyEnabled,
  showGrid,
  setShowGrid,
  showShape,
  setShowShape,
  pxPerMeter,
  setPxPerMeter,
  effectSettings,
  setEffectSettings,
  onToggleFogHidden,
  title = "Editor UI",
  defaultOpen = true,
}) {
  const [mode, setMode] = useState(() => {
    try { return (localStorage.getItem(UI_FIRE_MODE_KEY) || "infinite").toLowerCase(); }
    catch { return "infinite"; }
  });

  const [timedDur, setTimedDur] = useState(() => {
    try { return toNum(localStorage.getItem(UI_TIMED_DUR_KEY), 2.0); } catch { return 2.0; }
  });

  const [originX, setOriginX] = useState(() => {
    try { return toNum(localStorage.getItem(UI_PICKED_X_KEY), 0); } catch { return 0; }
  });
  const [originY, setOriginY] = useState(() => {
    try { return toNum(localStorage.getItem(UI_PICKED_Y_KEY), 0); } catch { return 0; }
  });
  const [angleDeg, setAngleDeg] = useState(() => {
    try { return toNum(localStorage.getItem(UI_PICKED_ANGLE_KEY), 0); } catch { return 0; }
  });

  useEffect(() => { try { localStorage.setItem(UI_FIRE_MODE_KEY, mode); } catch {} }, [mode]);
  useEffect(() => { try { localStorage.setItem(UI_TIMED_DUR_KEY, String(timedDur)); } catch {} }, [timedDur]);

  useEffect(() => { try { localStorage.setItem(UI_PICKED_X_KEY, String(originX)); } catch {} }, [originX]);
  useEffect(() => { try { localStorage.setItem(UI_PICKED_Y_KEY, String(originY)); } catch {} }, [originY]);
  useEffect(() => { try { localStorage.setItem(UI_PICKED_ANGLE_KEY, String(angleDeg)); } catch {} }, [angleDeg]);

  const fogControlled = typeof effectSettings?.fogHidden === "boolean";
  const [hideFogLocal, setHideFogLocal] = useState(() => {
    if (fogControlled) return false;
    try { return localStorage.getItem(UI_HIDE_FOG_KEY) === "1"; } catch { return false; }
  });
  const hideFogValue = fogControlled ? !!effectSettings.fogHidden : !!hideFogLocal;

  const handleFogChange = (next) => {
    const val = !!next;
    try { localStorage.setItem(UI_HIDE_FOG_KEY, val ? "1" : "0"); } catch {}
    try { window.dispatchEvent(new CustomEvent("EmitterTool:UI:HideFog", { detail: { hidden: val } })); } catch {}
    if (fogControlled) {
      if (typeof onToggleFogHidden === "function") onToggleFogHidden(val);
      else if (typeof setEffectSettings === "function") setEffectSettings((cur) => ({ ...(cur || {}), fogHidden: val }));
    } else {
      setHideFogLocal(val);
      if (typeof setEffectSettings === "function") setEffectSettings((cur) => ({ ...(cur || {}), fogHidden: val }));
    }
  };

  const markPlay = (newMode) => {
    try { localStorage.setItem(UI_LAST_PLAY_KEY, JSON.stringify({ mode: newMode, ts: Date.now() })); } catch {}
    try { window.dispatchEvent(new CustomEvent("EmitterTool:ModeChange", { detail: { mode: newMode } })); } catch {}
  };
  const writeModeSync = (m) => { try { localStorage.setItem(UI_FIRE_MODE_KEY, m); } catch {} };

  const sendOverlayConfig = (opts = {}) => {
    const forceHide = !!opts.forceHide;
    const shape = "circle";

    const baseShapeSettings = {
      ...(effectSettings?.shapeSettings || effectSettings?.shape || {}),
    };
    if (baseShapeSettings.radius == null) baseShapeSettings.radius = 1;

    const settings =
      mode === "rule"
        ? { ...baseShapeSettings, pulseEnabled: false, __freezePulse: true, centerLocked: true }
        : { ...baseShapeSettings };

    const show = forceHide ? false : true;

    try {
      window.dispatchEvent(
        new CustomEvent("EmitterTool:UI:EmitterOverlayConfig", {
          detail: { show, shape, settings },
        })
      );
    } catch {}
  };

  const fireStop = () => {
    writeModeSync("off");
    setMode("off");
    markPlay("off");
    try { window.dispatchEvent(new CustomEvent("EmitterTool:FireStop")); } catch {}
    sendOverlayConfig();
  };

  const fireInfinite = () => {
    writeModeSync("infinite");
    setMode("infinite");
    markPlay("infinite");
    try { window.dispatchEvent(new CustomEvent("EmitterTool:InfiniteStart")); } catch {}
    sendOverlayConfig();
  };

  const fireTimed = (durationS) => {
    const dur = Math.max(0.05, Number(durationS) || timedDur || 2);
    try { localStorage.setItem(UI_TIMED_DUR_KEY, String(dur)); } catch {}
    writeModeSync("timed");
    setTimedDur(dur);
    setMode("timed");
    markPlay("timed");
    try { window.dispatchEvent(new CustomEvent("EmitterTool:TimedStart", { detail: { durationS: dur } })); } catch {}
    sendOverlayConfig();
  };

  const fireRule = () => {
    writeModeSync("rule");
    setMode("rule");
    markPlay("rule");
    const meta = { origin: { x: originX, y: originY }, angleDeg };
    try { window.dispatchEvent(new CustomEvent("EmitterTool:FireRule", { detail: meta })); } catch {}
    sendOverlayConfig({ forceHide: true });
  };

  // Stage 7: Stage resize must only update columns/rows — NOT pxPerMeter
  const [stageSize, setStageSize] = useState([0, 0]);
  useEffect(() => {
    const onSize = (e) => {
      const arr = e?.detail?.size;
      if (Array.isArray(arr) && arr.length >= 2) setStageSize(arr);
    };
    window.addEventListener("EmitterTool:UI:ContainerSize", onSize);
    return () => window.removeEventListener("EmitterTool:UI:ContainerSize", onSize);
  }, []);


  const [gridSettings, setGridSettings] = useState(() => {
    try {
      const stored = localStorage.getItem(UI_GRID_SETTINGS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Stage 1: Ensure initial pxPerMeter is read from square_length, not cols
        // Stage 5: pxPerMeter must always be written to gridSettings.square_length
        if (parsed.square_length > 0) {
          setPxPerMeter?.(parsed.square_length);
        }
        return { ...parsed };
      }
    } catch {}
    return {
      cell_cols: 16, // Default columns (will be overwritten by derive logic)
      cell_rows: 10, // Default rows (will be overwritten by derive logic)
      color: "#ffffff",
      opacity: 0.25,
      offset_x: 0,
      offset_y: 0,
      square_length: 50, // Initial default for pxPerMeter
      grid_shape: "square",
      start_x: 0,
      start_y: 0,
    };
  });

  // Stage 5: pxPerMeter must always be written to gridSettings.square_length
  useEffect(() => {
    if (!Number.isFinite(pxPerMeter) || pxPerMeter <= 0) return;
    setGridSettings((g) => {
      // Don't modify g.cell_cols or g.cell_rows here, they are derived by the effect below.
      // ONLY update square_length.
      if (g.square_length === pxPerMeter) return g;
      const next = { ...g, square_length: pxPerMeter };
      try { localStorage.setItem(UI_GRID_SETTINGS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [pxPerMeter]);

  // Stage 6: emitGridConfig must use derived cols/rows
  const emitGridConfig = (currentGridSettings, currentPxPerMeter, stageWidth, stageHeight) => {
    const squareLenPx = currentPxPerMeter;
    
    // Stage 3 & 6: Columns and rows must be derived only
    const cols = Math.max(2, Math.round(stageWidth / squareLenPx));
    const rows = Math.max(1, Math.round(stageHeight / squareLenPx));

    const cfg = {
      ...currentGridSettings,
      cell_cols: cols,
      cell_rows: rows,
      square_length: squareLenPx,
    };

    try {
      window.dispatchEvent(
        new CustomEvent("EmitterTool:UI:GridConfigChange", {
          detail: { config: cfg }
        })
      );
    } catch {}
    
    // Stage 3: Only save derived cols/rows into gridSettings for display/LS
    setGridSettings((g) => {
      const next = { ...g };
      if (next.cell_cols === cols && next.cell_rows === rows) {
        return g;
      }
      next.cell_cols = cols;
      next.cell_rows = rows;
      // square_length is already updated by the pxPerMeter effect
      try { localStorage.setItem(UI_GRID_SETTINGS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Stage 4 & 7: Only flow allowed is pxPerMeter or Stage resize -> derive cols/rows -> emit config.
  // This useEffect covers both Stage resize and pxPerMeter change, but only modifies cols/rows for grid config/settings.
  // It does NOT call setPxPerMeter.
  useEffect(() => {
    const [w, h] = stageSize;
    if (!w || !h || !pxPerMeter || pxPerMeter <= 0) return;

    // Use the latest gridSettings and pxPerMeter state values
    emitGridConfig(gridSettings, pxPerMeter, w, h);
  }, [stageSize[0], stageSize[1], pxPerMeter, gridSettings.square_length]); // eslint-disable-line

  // Initial and request handler logic
  useEffect(() => {
    const [w, h] = stageSize;
    const initialPxPerMeter = gridSettings.square_length || pxPerMeter || 50;
    
    // Initial emit config (relies on pxPerMeter being set, either from props or from gridSettings)
    // The main effect above will handle the actual logic once all props/state are stable.
    if (w > 0 && h > 0 && initialPxPerMeter > 0) {
      // Don't need to re-emit here if the dependency effect handles it, but keep the request handler.
    }

    const handler = () => emitGridConfig(
      gridSettings,
      pxPerMeter || gridSettings.square_length || 50,
      stageSize[0],
      stageSize[1]
    );
    window.addEventListener("EmitterTool:UI:RequestGridConfig", handler);
    return () => window.removeEventListener("EmitterTool:UI:RequestGridConfig", handler);
  }, [gridSettings, pxPerMeter, stageSize]); // eslint-disable-line


  useEffect(() => {
    try {
      const w = localStorage.getItem(UI_WALLS_KEY);
      if (w != null) setWallsEnabled?.(w === "1");

      const t = localStorage.getItem(UI_TOKEN_KEY);
      if (t != null) setTokenEnabled?.(t === "1");

      const e = localStorage.getItem(UI_ENEMY_TOKEN_KEY);
      if (e != null) setEnemyEnabled?.(e === "1");

      const g = localStorage.getItem(UI_GRID_KEY);
      if (g != null) setShowGrid?.(g === "1");
      else {
        setShowGrid?.(true);
        try { localStorage.setItem(UI_GRID_KEY, "1"); } catch {}
      }

      const s = localStorage.getItem(UI_SHOW_SHAPE_KEY);
      if (s != null) setShowShape?.(s === "1");
    } catch {}
  }, [setWallsEnabled, setTokenEnabled, setEnemyEnabled, setShowGrid, setShowShape]);

  useEffect(() => { try { localStorage.setItem(UI_WALLS_KEY, wallsEnabled ? "1" : "0"); } catch {} }, [wallsEnabled]);
  useEffect(() => { try { localStorage.setItem(UI_TOKEN_KEY, tokenEnabled ? "1" : "0"); } catch {} }, [tokenEnabled]);
  useEffect(() => { try { localStorage.setItem(UI_ENEMY_TOKEN_KEY, enemyEnabled ? "1" : "0"); } catch {} }, [enemyEnabled]);
  useEffect(() => { try { localStorage.setItem(UI_GRID_KEY, showGrid ? "1" : "0"); } catch {} }, [showGrid]);
  useEffect(() => { try { localStorage.setItem(UI_SHOW_SHAPE_KEY, showShape ? "1" : "0"); } catch {} }, [showShape]);

  const resetUIOnly = () => {
    try { localStorage.removeItem(UI_HIDE_FOG_KEY); } catch {}
    if (!fogControlled) setHideFogLocal(false);
  };

  // Stage 8: Delete onColsChange and computePxPerMeterFromCols
  // const computePxPerMeterFromCols = (cols) => { ... } // DELETED
  // const onColsChange = (n) => { ... } // DELETED

  const [w, h] = stageSize;
  const currentPxPerMeter = Number.isFinite(pxPerMeter) && pxPerMeter > 0 ? pxPerMeter : 50;

  // Stage 3: Columns and rows are derived only
  const derivedCols = Math.round(w / currentPxPerMeter);
  const derivedRows = Math.round(h / currentPxPerMeter);

  const ppmText = Math.round(currentPxPerMeter);

  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    const onRequestOverlay = () => sendOverlayConfig();
    window.addEventListener("EmitterTool:UI:RequestEmitterOverlayConfig", onRequestOverlay);

    try {
      const m = (localStorage.getItem(UI_FIRE_MODE_KEY) || "infinite").toLowerCase();
      if (m === "infinite") fireInfinite();
      else if (m === "timed") {
        const dur = toNum(localStorage.getItem(UI_TIMED_DUR_KEY), timedDur);
        fireTimed(dur);
      } else if (m === "rule") {
        sendOverlayConfig();
        fireRule();
      } else {
        fireStop();
      }
    } catch {}

    return () => {
      window.removeEventListener("EmitterTool:UI:RequestEmitterOverlayConfig", onRequestOverlay);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    sendOverlayConfig();
  }, [mode, pxPerMeter, effectSettings]); // eslint-disable-line

  // Stage 9: New pxPerMeter slider component handler
  const onPxPerMeterChange = (newPx) => {
    setPxPerMeter?.(Math.max(10, Math.min(200, Math.round(newPx))));
    // The useEffect listening to pxPerMeter will handle gridSettings update and emitGridConfig
  };


  return (
    <CollapsibleBox title={title} defaultOpen={defaultOpen}>
      <div style={{ ...label, marginTop: 2 }}>Mode</div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12 }}>
          <input type="radio" checked={mode === "off"} onChange={() => fireStop()} /> Off
        </label>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12 }}>
          <input type="radio" checked={mode === "infinite"} onChange={() => fireInfinite()} /> Infinite
        </label>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12 }}>
          <input type="radio" checked={mode === "timed"} onChange={() => fireTimed(timedDur)} /> Timed
        </label>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12 }}>
          <input type="radio" checked={mode === "rule"} onChange={() => fireRule()} /> Emitter rule
        </label>
      </div>

      <div style={{ display: mode === "timed" ? "block" : "none", marginBottom: 10 }}>
        <div style={{ ...label, marginBottom: 4 }}>Timed duration (seconds)</div>
        <Slider value={timedDur} onChange={setTimedDur} min={0.05} max={120} step={0.05} />
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <button onClick={() => fireTimed(timedDur)} style={btn}>Start timed</button>
          <button onClick={fireStop} style={btnGhost}>Stop</button>
          <button onClick={() => setTimedDur(2)} style={btnSmall}>2s</button>
          <button onClick={() => setTimedDur(5)} style={btnSmall}>5s</button>
          <button onClick={() => setTimedDur(10)} style={btnSmall}>10s</button>
        </div>
      </div>

      {/* Stage 2 & 9: Replace "Grid width (columns)" with "Pixels per metre" slider */}
      <div style={{ marginTop: 10, marginBottom: 8 }}>
        <Slider
          label="Pixels per metre"
          value={currentPxPerMeter}
          onChange={onPxPerMeterChange}
          min={10}
          max={200}
          step={1}
          suffix="px"
        />

        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginTop: 10, opacity: 0.8 }}>
          <span>Derived columns (read-only)</span>
          <span title={`Stage Width: ${w}px / ${ppmText}px`}>{derivedCols} cols</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginTop: 4, opacity: 0.8 }}>
          <span>Derived rows (read-only)</span>
          <span title={`Stage Height: ${h}px / ${ppmText}px`}>{derivedRows} rows</span>
        </div>
        <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
          Assumption: <b>1 square = 1 metre</b>. Changing pixels per metre changes square size and derived columns/rows.
        </div>
      </div>
      {/* End of new pxPerMeter UI */}

      <div style={row}>
        <label style={{ fontSize: 12 }}>Show grid</label>
        <input
          type="checkbox"
          checked={!!showGrid}
          onChange={(e) => setShowGrid?.(e.target.checked)}
          style={{ accentColor: "#64748b" }}
        />
      </div>
      <div style={row}>
        <label style={{ fontSize: 12 }}>Walls enabled</label>
        <input
          type="checkbox"
          checked={!!wallsEnabled}
          onChange={(e) => setWallsEnabled?.(e.target.checked)}
          style={{ accentColor: "#64748b" }}
        />
      </div>
      <div style={row}>
        <label style={{ fontSize: 12 }}>Caster token (blue) visible</label>
        <input
          type="checkbox"
          checked={!!tokenEnabled}
          onChange={(e) => setTokenEnabled?.(e.target.checked)}
          style={{ accentColor: "#64748b" }}
        />
      </div>
      <div style={row}>
        <label style={{ fontSize: 12 }}>Enemy token (red) visible</label>
        <input
          type="checkbox"
          checked={!!enemyEnabled}
          onChange={(e) => setEnemyEnabled?.(e.target.checked)}
          style={{ accentColor: "#64748b" }}
        />
      </div>
      <div style={row}>
        <label style={{ fontSize: 12 }}>Hide fog</label>
        <input
          type="checkbox"
          checked={!!hideFogValue}
          onChange={(e) => handleFogChange(e.target.checked)}
          style={{ accentColor: "#64748b" }}
        />
      </div>
      <div style={row}>
        <label style={{ fontSize: 12 }}>Show emitter shape</label>
        <input
          type="checkbox"
          checked={!!showShape}
          onChange={(e) => setShowShape?.(e.target.checked)}
          style={{ accentColor: "#64748b" }}
        />
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button onClick={resetUIOnly} title="Clear UI-only flags stored in your browser" style={btnGhost}>
          Reset UI-only flags
        </button>
        <div style={{ fontSize: 11, opacity: 0.7 }}>(These options won’t be saved with exports)</div>
      </div>
    </CollapsibleBox>
  );
}

const numInputStyle = {
  background: "#0b0d12",
  color: "white",
  border: "1px solid #374151",
  borderRadius: 6,
  padding: "6px 8px",
  fontSize: 12,
  boxSizing: "border-box",
  width: "100%",
};
const btn = {
  background: "#2563eb",
  color: "white",
  border: "1px solid #1d4ed8",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 12,
  cursor: "pointer",
};
const btnSmall = {
  background: "#334155",
  color: "white",
  border: "1px solid #475569",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 12,
  cursor: "pointer",
};
const btnGhost = {
  background: "transparent",
  color: "#cbd5e1",
  border: "1px solid #334155",
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 12,
  cursor: "pointer",
};

export { EditorUIPanel };